import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Minus, Plus } from 'lucide-react';

import ScrubIcon from '@/core/assets/oot/ennemies/OoT_Deku_Scrub_Artwork.png';

interface ScrubsCounterModalProps {
  entranceId: string;
  count: number;
  onChange: (entranceId: string, count: number) => void;
  onClose: () => void;
  anchorRect: { top: number; left: number };
}

export function ScrubsCounterModal({ entranceId, count, onChange, onClose, anchorRect }: ScrubsCounterModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const decrement = () => {
    if (count > 0) onChange(entranceId, count - 1);
  };

  const increment = () => {
    onChange(entranceId, count + 1);
  };

  return createPortal(
    <div
      ref={ref}
      className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 flex items-center gap-3"
      style={{ top: anchorRect.top, left: anchorRect.left + 230, zIndex: 10000 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <img src={ScrubIcon} alt="Scrubs" className="w-8 h-8 object-contain" />
      <div className="flex items-center gap-2">
        <button
          onClick={decrement}
          disabled={count <= 0}
          className="w-6 h-6 rounded flex items-center justify-center bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <Minus size={12} />
        </button>
        <span className="text-lg font-bold text-gray-200 w-6 text-center">{count}</span>
        <button
          onClick={increment}
          className="w-6 h-6 rounded flex items-center justify-center bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
        >
          <Plus size={12} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
